import { useState } from "react";
import { Phone } from "lucide-react";
import { useGetIdentity } from "ra-core";
import { IconButton } from "@/components/ui/icon-button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { hasMemberCapability } from "@/components/atomic-crm/providers/commons/memberModuleAccess";
import type { AccessIdentity } from "@/components/atomic-crm/providers/commons/canAccess";
import { NewConversationDialog } from "@/modules/messages/NewConversationDialog";
import { useMessagingEnabled } from "@/modules/messages/useMessagingEnabled";
import { useVoiceCallContextOptional } from "@/modules/voice/voiceCallContext";

/** Top bar phone icon: opens New conversation (pick a contact or dial). */
export const GlobalDialButton = () => {
  const { identity } = useGetIdentity();
  const { smsEnabled, voiceEnabled, isPending } = useMessagingEnabled();
  const voice = useVoiceCallContextOptional();
  const [open, setOpen] = useState(false);

  const canCall =
    voiceEnabled &&
    hasMemberCapability(
      identity as AccessIdentity | undefined,
      "voice.calls.make",
    );

  if (isPending || (!smsEnabled && !canCall)) return null;

  const onCall = voice?.callState === "open" || voice?.isBusy;

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <IconButton
            variant="ghost"
            className="relative size-9 data-[state=open]:bg-accent"
            aria-label="New conversation"
            onClick={() => setOpen(true)}
          >
            <Phone className="size-4" />
            {onCall ? (
              <span className="absolute top-1 right-1 size-2 rounded-full bg-success ring-2 ring-background" />
            ) : null}
          </IconButton>
        </TooltipTrigger>
        <TooltipContent>New conversation</TooltipContent>
      </Tooltip>
      <NewConversationDialog open={open} onOpenChange={setOpen} />
    </>
  );
};
